import api from './api';
import { mcpService } from './mcpService';
import type {
  Conversation,
  Message,
  MessageAttachment,
  ConversationListParams,
  CreateConversationRequest,
  UpdateConversationRequest,
  ChatResponse,
} from '../types/assistant';

const BASE = '/assistant';

export const assistantService = {
  // -----------------------------------------------------------------------
  // Conversations
  // -----------------------------------------------------------------------

  listConversations: async (params?: ConversationListParams): Promise<Conversation[]> => {
    const response = await api.get<Conversation[]>(`${BASE}/conversations`, {
      params: { limit: params?.limit ?? 30, offset: params?.offset ?? 0 },
    });
    return response.data;
  },

  createConversation: async (data?: CreateConversationRequest): Promise<Conversation> => {
    const response = await api.post<Conversation>(`${BASE}/conversations`, data ?? {});
    return response.data;
  },

  updateConversation: async (id: string, data: UpdateConversationRequest): Promise<void> => {
    await api.patch(`${BASE}/conversations/${id}`, data);
  },

  deleteConversation: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/conversations/${id}`);
  },

  // -----------------------------------------------------------------------
  // Messages
  // -----------------------------------------------------------------------

  getMessages: async (conversationId: string, limit = 200): Promise<Message[]> => {
    const response = await api.get<Message[]>(
      `${BASE}/conversations/${conversationId}/messages`,
      { params: { limit } }
    );
    return response.data;
  },

  // -----------------------------------------------------------------------
  // Chat (via MCP server)
  // -----------------------------------------------------------------------

  // -----------------------------------------------------------------------
  // Image attachments (PRD 85)
  // -----------------------------------------------------------------------

  uploadAssistantImage: async (file: File): Promise<MessageAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<MessageAttachment>('/uploads/assistant/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getCapabilities: async (): Promise<{ vision_supported: boolean; model: string }> => {
    try {
      const res = await fetch('/capabilities');
      if (!res.ok) return { vision_supported: false, model: '' };
      return await res.json();
    } catch {
      return { vision_supported: false, model: '' };
    }
  },

  /**
   * Sends a message through the MCP /chat endpoint using server-side history.
   * The MCP server persists messages in the backend.
   */
  sendMessage: async (
    conversationId: string,
    message: string,
    attachments?: MessageAttachment[]
  ): Promise<ChatResponse> => {
    const activeKey = mcpService.getActiveKey();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (activeKey) {
      headers['X-MCP-API-Key'] = activeKey;
    }

    const response = await fetch('/chat', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        conversation_id: conversationId,
        message,
        attachments: attachments && attachments.length > 0
          ? attachments.map(a => ({ path: a.path, mime: a.mime, name: a.name, size: a.size }))
          : undefined,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Chat request failed (${response.status})`);
    }

    return response.json();
  },
};
